import React from 'react';
import styled from 'styled-components';
import { appTheme } from '../styles/theme';

export type FeatureIconId = 'templates' | 'ai' | 'editor' | 'present';

const ICON_TINTS: Record<FeatureIconId, string> = {
  templates: '#dceee8',
  ai: '#d4ebf7',
  editor: '#fae8dc',
  present: '#fff0e3',
};

const STROKE = appTheme.greenDark;

const Frame = styled.div<{ $tint: string }>`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: radial-gradient(circle at 28% 22%, #ffffff 0%, ${({ $tint }) => $tint} 68%);
  box-shadow:
    0 8px 20px rgba(10, 46, 40, 0.08),
    0 1px 2px rgba(10, 46, 40, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Svg = styled.svg`
  width: 28px;
  height: 28px;
  display: block;
`;

const iconProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: STROKE,
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const TemplatesIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <rect x="4" y="3" width="12" height="16" rx="1.5" />
    <path d="M8 7h4M8 10.5h4M8 14h2.5" />
    <path d="M16 7.5 20 5.5v13l-4-2" />
  </Svg>
);

/** Sparkle cluster — widely recognized AI / generative iconography. */
const AiIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <path d="M12 4.5v4M12 15.5v4.5" />
    <path d="M7.5 12H4M20 12h-3.5" />
    <path d="M9.2 9.2 6.8 6.8M17.2 17.2l-2.4-2.4" />
    <path d="M14.8 9.2l2.4-2.4M6.8 17.2l2.4-2.4" />
    <path d="M18.2 6.2v2M17.2 7.2h2" />
    <path d="M5.8 17.8v1.4M5.1 18.5h1.4" />
  </Svg>
);

const EditorIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <rect x="3.5" y="4" width="17" height="14" rx="2" />
    <path d="M8 16.5 15.5 9l2 2L10 18.5H8v-2z" />
    <path d="M14 9.5l1.5-1.5 2 2L16 11.5" />
    <path d="M7 8h3" />
  </Svg>
);

const PresentIcon = () => (
  <Svg {...iconProps} aria-hidden>
    <rect x="3" y="5" width="18" height="11" rx="1.5" />
    <path d="M8 19.5h8" />
    <path d="M12 16v3.5" />
    <path d="M9.5 12.5 11 11l1.8 2.2L15.5 10" />
  </Svg>
);

const ICONS: Record<FeatureIconId, React.FC> = {
  templates: TemplatesIcon,
  ai: AiIcon,
  editor: EditorIcon,
  present: PresentIcon,
};

interface FeatureIconProps {
  id: FeatureIconId;
  className?: string;
}

const FeatureIcon: React.FC<FeatureIconProps> = ({ id, className }) => {
  const Icon = ICONS[id];

  return (
    <Frame $tint={ICON_TINTS[id]} className={className} aria-hidden>
      <Icon />
    </Frame>
  );
};

export default FeatureIcon;
